'use client'

import { createContext, useContext, useEffect, useRef, useState, useCallback, ReactNode } from 'react'

/**
 * Декларация для доступа к переменным окружения в браузерной сборке (Next.js).
 * Нужна, чтобы TypeScript не ругался на отсутствие `process` без обязательной установки `@types/node`.
 */
declare const process:
  | {
      env?: Record<string, string | undefined>
    }
  | undefined

interface CeleryTaskEvent {
  type: string
  event_type?: string
  task_id: string
  task_name?: string
  status: 'success' | 'failed'
  message: string
  result?: any
  error?: string
  timestamp?: string
}

/**
 * Универсальный формат уведомления от бэкенда.
 */
interface Notification {
  type: 'notification'
  event:
    | 'export_completed'
    | 'import_completed'
    | 'invoice_document_ready'
    | 'task_completed'
    | 'task_failed'
    | 'notification_created'
    | 'notification_updated'
    | 'notification_deleted'
  data: {
    task_id?: string
    task_name?: string
    task_type?: string
    shop_id?: string
    result?: any
    error?: string
    timestamp?: string
    id?: string
    title?: string
    body_markdown?: string
    image_url?: string | null
    is_important?: boolean
    is_deleted?: boolean
    created_at?: string
    updated_at?: string
  }
}

/**
 * Callback для обработки уведомлений.
 */
type NotificationCallback = (notification: Notification) => void

interface CeleryWebSocketContextType {
  isConnected: boolean
  subscribe: (taskId: string, callback: (event: CeleryTaskEvent) => void) => () => void
  unsubscribe: (taskId: string) => void
  onNotification: (callback: NotificationCallback) => () => void
}

const CeleryWebSocketContext = createContext<CeleryWebSocketContextType | undefined>(undefined)

export function CeleryWebSocketProvider({ children }: { children: ReactNode }) {
  const [isConnected, setIsConnected] = useState(false)
  const wsRef = useRef<WebSocket | null>(null)
  const subscribersRef = useRef<Map<string, Set<(event: CeleryTaskEvent) => void>>>(new Map())
  const notificationCallbacksRef = useRef<Set<NotificationCallback>>(new Set())
  /**
   * Таймер переподключения. Держим строго один, чтобы не было "пачки" попыток в секунду.
   */
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const reconnectAttemptsRef = useRef(0)
  const maxReconnectAttempts = 5
  const reconnectDelayMs = 3000 // 3 секунды
  const isConnectingRef = useRef(false) // Флаг для предотвращения множественных подключений
  const connectRef = useRef<() => void>(() => undefined)

  /**
   * Планирует переподключение с фиксированной задержкой.
   * Важно: переподключение планируется ТОЛЬКО через этот метод.
   */
  const scheduleReconnect = useCallback((reason: string) => {
    if (reconnectTimeoutRef.current) {
      return
    }
    if (reconnectAttemptsRef.current >= maxReconnectAttempts) {
      console.error('[CeleryWebSocket] Достигнут лимит попыток переподключения:', maxReconnectAttempts)
      return
    }

    reconnectAttemptsRef.current += 1
    console.log(
      `[CeleryWebSocket] Попытка переподключения ${reconnectAttemptsRef.current}/${maxReconnectAttempts} через ${reconnectDelayMs}ms (${reason})...`,
    )

    reconnectTimeoutRef.current = setTimeout(() => {
      reconnectTimeoutRef.current = null
      connectRef.current()
    }, reconnectDelayMs)
  }, [])

  const connect = useCallback(() => {
    // Если уже подключены/подключаемся — не создаём новый сокет
    if (wsRef.current?.readyState === WebSocket.OPEN || wsRef.current?.readyState === WebSocket.CONNECTING) {
      return
    }
    if (isConnectingRef.current) {
      return
    }

    // Получаем токен из localStorage
    const token = localStorage.getItem('access_token')
    if (!token) {
      console.warn('[CeleryWebSocket] Токен не найден, пропускаем подключение')
      return
    }

    // Формируем URL для WebSocket
    // Проверяем переменную окружения для прямого подключения к бэкенду
    const apiUrl = typeof process !== 'undefined' && process.env 
      ? (process.env.NEXT_PUBLIC_API_URL || '').trim()
      : ''
    
    let wsUrl: string
    
    if (apiUrl) {
      // Убираем /api из конца, если есть
      const baseUrl = apiUrl.replace(/\/api\/?$/, '')
      const url = new URL(baseUrl)
      const protocol = url.protocol === 'https:' ? 'wss:' : 'ws:'
      wsUrl = `${protocol}//${url.host}/api/v1/shops/ws/notifications?token=${encodeURIComponent(token)}`
    } else {
      // Определяем автоматически
      const isDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
      
      if (isDev) {
        // В dev режиме используем прямой URL бэкенда
        const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
        wsUrl = `${protocol}//localhost:8000/api/v1/shops/ws/notifications?token=${encodeURIComponent(token)}`
      } else {
        // В production используем относительный путь (через nginx прокси)
        const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
        const host = window.location.host
        wsUrl = `${protocol}//${host}/api/v1/shops/ws/notifications?token=${encodeURIComponent(token)}`
      }
    }

    try {
      isConnectingRef.current = true
      const ws = new WebSocket(wsUrl)

      ws.onopen = () => {
        setIsConnected(true)
        isConnectingRef.current = false
        // Сбрасываем счетчик только при реальном успешном подключении.
        reconnectAttemptsRef.current = 0
        console.log('[CeleryWebSocket] Подключено')
      }

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data)
          
          // Обрабатываем ping - отправляем pong
          if (data.type === 'ping') {
            try {
              ws.send(JSON.stringify({ type: 'pong' }))
            } catch (error) {
              console.error('[CeleryWebSocket] Ошибка отправки pong:', error)
            }
            return
          }
          
          // Обрабатываем уведомления (новый формат)
          if (data.type === 'notification') {
            const notification = data as Notification
            // Вызываем все зарегистрированные callbacks для уведомлений
            notificationCallbacksRef.current.forEach((callback: NotificationCallback) => {
              try {
                callback(notification)
              } catch (error) {
                console.error('[CeleryWebSocket] Ошибка в notification callback:', error)
              }
            })
          }
          
          // Обрабатываем события задач (старый формат)
          if (data.type === 'celery_task_event' && data.task_id) {
            // Уведомляем всех подписчиков этой задачи
            const callbacks = subscribersRef.current.get(data.task_id)
            if (callbacks) {
              callbacks.forEach((callback: (event: CeleryTaskEvent) => void) => {
                try {
                  callback(data as CeleryTaskEvent)
                } catch (error) {
                  console.error('[CeleryWebSocket] Ошибка в callback:', error)
                }
              })
            }
          }
        } catch (error) {
          console.error('[CeleryWebSocket] Ошибка парсинга:', error)
        }
      }

      ws.onerror = (error) => {
        console.error('[CeleryWebSocket] Ошибка WebSocket:', error)
        // Важно: переподключение планируем только в onclose/catch, иначе можно получить "дубли".
      }

      ws.onclose = (event) => {
        setIsConnected(false)
        isConnectingRef.current = false
        console.log('[CeleryWebSocket] Соединение закрыто', event.code, event.reason)
        
        // Очищаем текущее соединение
        wsRef.current = null

        // Переподключаемся только если закрытие НЕ нормальное (1000)
        if (event.code !== 1000) {
          scheduleReconnect(`onclose code=${event.code}`)
        }
      }

      wsRef.current = ws
    } catch (error) {
      console.error('[CeleryWebSocket] Ошибка создания WebSocket:', error)
      setIsConnected(false)
      isConnectingRef.current = false

      scheduleReconnect('catch')
    }
  }, [scheduleReconnect])

  const subscribe = useCallback((taskId: string, callback: (event: CeleryTaskEvent) => void) => {
    if (!subscribersRef.current.has(taskId)) {
      subscribersRef.current.set(taskId, new Set())
    }
    subscribersRef.current.get(taskId)!.add(callback)

    // Возвращаем функцию отписки
    return () => {
      const callbacks = subscribersRef.current.get(taskId)
      if (callbacks) {
        callbacks.delete(callback)
        if (callbacks.size === 0) {
          subscribersRef.current.delete(taskId)
        }
      }
    }
  }, [])

  const unsubscribe = useCallback((taskId: string) => {
    subscribersRef.current.delete(taskId)
  }, [])

  /**
   * Регистрирует callback для получения всех уведомлений.
   * Важно: не вызывает connect(). Подключение управляется только эффектом и scheduleReconnect().
   */
  const onNotification = useCallback((callback: NotificationCallback) => {
    notificationCallbacksRef.current.add(callback)
    
    // Возвращаем функцию отписки
    return () => {
      notificationCallbacksRef.current.delete(callback)
    }
  }, [])

  // Подключаемся при монтировании компонента
  useEffect(() => {
    connectRef.current = connect
    connect()

    // Обработчик закрытия страницы - корректно закрываем WebSocket
    const handleBeforeUnload = () => {
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        // Используем send для отправки сообщения о закрытии перед закрытием
        try {
          wsRef.current.close(1000, 'Page unloading')
        } catch (error) {
          console.error('[CeleryWebSocket] Ошибка при закрытии:', error)
        }
      }
    }

    // Обработчик видимости страницы - закрываем при скрытии вкладки
    const handleVisibilityChange = () => {
      if (document.hidden && wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        // Не закрываем сразу, но можем оптимизировать позже
        // Пока оставляем соединение открытым для получения уведомлений
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      // Очищаем таймеры переподключения
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current)
        reconnectTimeoutRef.current = null
      }
      
      // Сбрасываем флаг подключения
      isConnectingRef.current = false
      
      // Корректно закрываем WebSocket
      if (wsRef.current) {
        try {
          // Если соединение открыто, закрываем с кодом 1000 (нормальное закрытие)
          if (wsRef.current.readyState === WebSocket.OPEN || wsRef.current.readyState === WebSocket.CONNECTING) {
            wsRef.current.close(1000, 'Component unmounting')
          }
        } catch (error) {
          console.error('[CeleryWebSocket] Ошибка при закрытии WebSocket:', error)
        }
        wsRef.current = null
      }
      
      window.removeEventListener('beforeunload', handleBeforeUnload)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [connect])

  return (
    <CeleryWebSocketContext.Provider value={{ isConnected, subscribe, unsubscribe, onNotification }}>
      {children}
    </CeleryWebSocketContext.Provider>
  )
}

export function useCeleryWebSocketContext() {
  const context = useContext(CeleryWebSocketContext)
  if (context === undefined) {
    throw new Error('useCeleryWebSocketContext must be used within a CeleryWebSocketProvider')
  }
  return context
}

